import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listConferimenti, type ConferimentoListItem } from '../../api/conferimenti';
import { getFattura } from '../../api/fatture';
import i18n from '../../i18n';

interface Props {
  ordineId: string;
  status: string;
  fatturatoOrdine?: boolean;
  pagatoOrdine?: boolean;
  conferimentiTotali?: number;
  conferimentiFatturati?: number;
  conferimentiPagati?: number;
}

const PILL = 'px-2 py-0.5 rounded-full text-xs font-medium';

// Identificativo: numero ufficiale (ora sempre assegnato all'emissione), con
// fallback al numero interno per eventuali fatture legacy.
function ConfStato({ c, numeroInterno }: { c: ConferimentoListItem; numeroInterno?: string | null }) {
  if (!c.fatturato) return <span className={`${PILL} bg-stone-bg text-ink-soft`}>{i18n.t('conferimenti.fatturazione_card.non_fatturato')}</span>;
  const id = c.numero_fattura ?? numeroInterno ?? i18n.t('conferimenti.fatturazione_card.in_attesa');
  if (c.stato_fattura === 'pagata') return <span className={`${PILL} bg-sage-light text-sage-dark`}>{i18n.t('conferimenti.fatturazione_card.pagata_id', { id })}</span>;
  return <span className={`${PILL} bg-amber-50 text-amber-700`}>{i18n.t('conferimenti.fatturazione_card.fatturato_id', { id })}</span>;
}

export default function FatturazioneOrdineCard({
  ordineId, status, fatturatoOrdine, pagatoOrdine,
  conferimentiTotali, conferimentiFatturati, conferimentiPagati,
}: Props) {
  const { t } = useTranslation();
  const [conf, setConf] = useState<ConferimentoListItem[]>([]);
  // Mappa fattura_id → numero_interno (il BE /conferimenti espone solo fattura_id).
  const [numeriInterni, setNumeriInterni] = useState<Record<string, string | null>>({});

  useEffect(() => {
    void (async () => {
      try {
        const res = await listConferimenti({ order_id: ordineId });
        setConf(res.items);
        const ids = [...new Set(res.items.map((c) => c.fattura_id).filter((x): x is string => !!x))];
        const fatt = await Promise.allSettled(ids.map((id) => getFattura(id)));
        const map: Record<string, string | null> = {};
        fatt.forEach((r, i) => { if (r.status === 'fulfilled') map[ids[i]] = r.value.numero_interno; });
        setNumeriInterni(map);
      } catch { /* la card resta col solo riepilogo aggregato */ }
    })();
  }, [ordineId]);

  const tot = conferimentiTotali ?? conf.length;
  const fat = conferimentiFatturati ?? conf.filter((c) => c.fatturato).length;
  const pag = conferimentiPagati ?? conf.filter((c) => c.stato_fattura === 'pagata').length;

  return (
    <div className="bg-white border border-stone-mid rounded-xl p-4" data-testid="card-fatturazione">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-ink-dark">{t('conferimenti.fatturazione_card.titolo')}</h3>
        {pagatoOrdine ? (
          <span className={`${PILL} bg-sage-light text-sage-dark`}>{t('conferimenti.fatturazione_card.pill_pagato')}</span>
        ) : fatturatoOrdine ? (
          <span className={`${PILL} bg-amber-50 text-amber-700`}>{t('conferimenti.fatturazione_card.pill_fatturato')}</span>
        ) : status === 'concluso' ? (
          <span className={`${PILL} bg-stone-bg text-ink-soft`}>{t('conferimenti.fatturazione_card.pill_da_fatturare')}</span>
        ) : null}
      </div>

      <div className="flex gap-4 text-xs text-ink-mid mb-3">
        <span>{t('conferimenti.fatturazione_card.fatturati')}: <strong className="text-ink-dark">{fat}/{tot}</strong></span>
        <span>{t('conferimenti.fatturazione_card.pagati')}: <strong className="text-ink-dark">{pag}/{tot}</strong></span>
      </div>

      {conf.length > 0 && (
        <ul className="space-y-1.5">
          {conf.map((c) => (
            <li key={c.id} className="flex items-center justify-between text-sm">
              <span className="font-medium text-ink-dark">{c.numero ?? '—'}</span>
              <ConfStato c={c} numeroInterno={c.fattura_id ? numeriInterni[c.fattura_id] : undefined} />
            </li>
          ))}
        </ul>
      )}
      <p className="text-[11px] text-ink-faint mt-2">{t('conferimenti.fatturazione_card.nota')}</p>
    </div>
  );
}
